import type { ConnectFailedReason } from '@/modules/pmt/components/ESConnectFailedDialog/ESConnectFailedDialog';
import { type TEmailSyncData, useEmailSyncAccounts } from '@/modules/pmt/hooks/useEmailSyncAccounts';
import { useIsEmailSyncAllowed } from '@/modules/pmt/hooks/useIsEmailSyncAllowed';
import useSyncingAccount from '@/modules/pmt/hooks/useSyncingAccount';
import { type Dispatch, type SetStateAction, createContext, useContext, useMemo } from 'react';
interface IEmailSyncContext {
  emailSyncData?: TEmailSyncData;
  isEmailSyncAllowed: boolean;
  isESAllowed: 'unitialized' | 'allowed' | 'not-allowed';
  hasEmailAccounts: boolean;
  hasEmailConnected: boolean;
  fetchEmailSyncAccounts: () => Promise<void>;
  connectEsFailedReason: ConnectFailedReason | null;
  setConnectEsFailedReason: Dispatch<SetStateAction<ConnectFailedReason | null>>;
  setSyncingAccount: Dispatch<SetStateAction<string | null>>;
  isSyncing: boolean;
  onDismissSyncing: () => void;
  isFetchingEmailAccounts: boolean;
  setIsFetchingEmailAccounts: Dispatch<SetStateAction<boolean>>;
}

const EmailSyncContext = createContext<IEmailSyncContext>({
  emailSyncData: undefined,
  isEmailSyncAllowed: false,
  isESAllowed: 'unitialized',
  hasEmailAccounts: false,
  hasEmailConnected: false,
  fetchEmailSyncAccounts: async () => Promise.resolve(),
  connectEsFailedReason: null,
  setConnectEsFailedReason: () => null,
  setSyncingAccount: () => null,
  isSyncing: false,
  onDismissSyncing: () => null,
  isFetchingEmailAccounts: false,
  setIsFetchingEmailAccounts: () => null,
});

export const EmailSyncProvider: React.FC = ({ children }) => {
  const { emailSyncData, fetchEmailSyncAccounts, isFetchingEmailAccounts, setIsFetchingEmailAccounts } =
    useEmailSyncAccounts();
  const { connectEsFailedReason, setConnectEsFailedReason, setSyncingAccount, isSyncing, onDismissSyncing } =
    useSyncingAccount();

  const hasEmailAccounts = !!emailSyncData?.emails?.length;
  const hasEmailConnected = !!emailSyncData?.hasEmailConnected;
  const { isEmailSyncAllowed, isESAllowed } = useIsEmailSyncAllowed({ emailSyncData, hasEmailAccounts });

  const value = useMemo(
    () => ({
      emailSyncData,
      isEmailSyncAllowed,
      isESAllowed,
      hasEmailAccounts,
      hasEmailConnected,
      fetchEmailSyncAccounts,
      connectEsFailedReason,
      setConnectEsFailedReason,
      setSyncingAccount,
      isSyncing,
      onDismissSyncing,
      isFetchingEmailAccounts,
      setIsFetchingEmailAccounts,
    }),
    [
      connectEsFailedReason,
      emailSyncData,
      hasEmailAccounts,
      hasEmailConnected,
      isEmailSyncAllowed,
      isESAllowed,
      fetchEmailSyncAccounts,
      setConnectEsFailedReason,
      setSyncingAccount,
      isSyncing,
      onDismissSyncing,
      isFetchingEmailAccounts,
      setIsFetchingEmailAccounts,
    ],
  );

  return <EmailSyncContext.Provider value={value}>{children}</EmailSyncContext.Provider>;
};

export const useEmailSyncContext = () => useContext(EmailSyncContext);
